import React, { useState, useEffect, useRef, useContext } from 'react';
import * as Sentry from '@sentry/browser';
import { useHistory } from 'react-router-dom';
import { events, CognitoIdentityCredentials } from 'aws-sdk/global';
import S3 from 'aws-sdk/clients/s3';
import { useForm } from 'react-hook-form';
import { JobProfile, ResumeUpload } from '../../utils/formNames';
import { AWSConfig, FORM_HOST, regularExpressions } from '../../utils/constants';
import { getFormData, storeFormData } from '../../utils/sessionStorage';
import EnrollmentContext from '../../contexts/EnrollmentContext';
import { trackFormError } from '../../utils/monitoring';
import { pardotFormSubmission, submitAllDataToSlowForm, fileMIMEType, pseudoGUID } from '../../utils/browserUtils';
import linkedinLogo from '../../images/linkedin-logo.svg';
import EnrollmentForm from './EnrollmentForm';
import EnrollmentFormInput from '../FormInputs/FormInput';
import EnrollmentFormFileUpload from '../FormInputs/FormFileUpload';
import EnrollmentFormInputFloating from '../FormInputs/FormInputFloating';
import EnrollmentFormSelectInput from '../FormInputs/FormSelectInput';
import EnrollmentFormError from '../FormInputs/FormError';
import androidCheck from '../../utils/mobileUtils';
import styles from '../../styles/EnrollmentForm.module.scss';

const { apiVersion, Bucket, region, IdentityPoolId, readACL } = AWSConfig;

const s3 = new S3({
  apiVersion,
  credentials: new CognitoIdentityCredentials({ IdentityPoolId }, { region }),
  params: { Bucket },
  maxRetries: 10,
  correctClockSkew: true,
  region,
});

const formName = JobProfile;
const pardotFormUrl = `${FORM_HOST}/l/772833/2020-04-07/5616yb`;
const callbackLocations = {
  success_location: `${FORM_HOST}/l/772833/2020-07-06/7k97k6/772833/58597/job_profile_success.js`,
  error_location: `${FORM_HOST}/l/772833/2020-07-06/7k97hd/772833/58589/job_profile_error.js`,
};

const wordOfMouth = 'Word of Mouth';
const pathriseAlumni = 'Pathrise Alumni Referral';
const emailFromPathrise = 'Email Invitation from Pathrise';
const linkedinFromPathrise = 'LinkedIn Message from Pathrise';
const googleAd = 'Google Ad';
const searchEngine = 'Search Engine';
const craigslist = 'Craigslist';
const jobSite = 'Job Site';
const onlineAd = 'Facebook/Instagram Ad';
const tiktok = 'TikTok';
const article = 'Article';
const myUniversity = 'My University';
const heardOther = 'Other';
const hearAboutUsRequiredFields = {};
hearAboutUsRequiredFields[pathriseAlumni] = true;
hearAboutUsRequiredFields[heardOther] = true;

const hearAboutOptions = [
  wordOfMouth,
  pathriseAlumni,
  emailFromPathrise,
  linkedinFromPathrise,
  googleAd,
  searchEngine,
  craigslist,
  jobSite,
  onlineAd,
  tiktok,
  article,
  myUniversity,
  heardOther,
];

const { phoneRegex } = regularExpressions;

const JobProfileForm = () => {
  const { register, handleSubmit, triggerValidation, getValues, formState, watch, errors } = useForm({
    mode: 'onBlur',
    submitFocusError: false,
  });

  const history = useHistory();

  const { page, userEmail, slowForm } = useContext(EnrollmentContext);

  // For the slowForm, when there is a file to be uploaded
  // the `asyncPreSubmit` prop on the EnrollmentForm component
  // must be `true`, and this function must return a Promise
  // that is resolved with the updated Form data when the
  // S3 upload is complete.
  const preSubmit = (data) => {
    if (data.linkedin_url) {
      data.linkedin_url = data.linkedin_url.trim();
    }
    if (!data.fellow_cv || !data.fellow_cv.length) {
      return null;
    }
    return new Promise((resolve, reject) => {
      const Body = data.fellow_cv[0];
      const hash = pseudoGUID(5);
      const Key = `cvs/${hash}_${Body.name}`;
      const ContentType = fileMIMEType(Key);
      const params = { ContentType, Key, Body, ACL: readACL };
      let errorCount = 0;
      s3.upload(params, s3UploadCallback);
      function s3UploadCallback(err, res) {
        if (err) {
          errorCount += 1;
          Sentry.withScope((scope) => {
            scope.setExtra('Fellow Email', userEmail);
            scope.setExtra('S3 Key', Key);
            scope.setExtra('ContentType', ContentType);
            scope.setExtra('UploadSize', Body.size);
            scope.setExtra('Bucket', Bucket);
            scope.setExtra('Region', region);
            scope.setExtra('errorObject', err);
            scope.setExtra('errorCount', errorCount);
            Sentry.captureMessage('Error uploading resume to S3 [v4]');
          });
          events.on('retry', (resp) => {
            Sentry.withScope((scope) => {
              scope.setExtra('Fellow Email', userEmail);
              scope.setExtra('respObject', resp);
              Sentry.captureMessage('Retrying resume upload to S3 [v4]');
            });
          });
          if (errorCount < 3) {
            s3.upload(params, s3UploadCallback);
          } else if (slowForm) {
            reject(err);
          } else {
            history.push(`/${page}`, {
              from: window.location.href,
              serverDidError: true,
              errorMessage:
                'An error occurred uploading your resume. Please check that the file type is correct and is smaller than 2MB.',
            });
          }
          // exit after error
          return;
        }
        // Keeping the fellow_cv FileList causes issues
        // with updating the <input>'s for slow submit.
        delete data.fellow_cv;
        data.resume_url = res.Location;
        // resolve promise with S3 data attached
        if (slowForm) {
          resolve(data);
          return;
        }
        // else: fastform continues asynchronous submission
        storeFormData(JobProfile, data);
        const resumeData = { email: userEmail, resume_url: res.Location };
        const callbackName = ResumeUpload;
        const resumeCallbackLocations = {
          success_location: `${FORM_HOST}/l/772833/2020-07-06/7k97k4/772833/58595/resume_upload_success.js`,
          error_location: `${FORM_HOST}/l/772833/2020-07-06/7k97k2/772833/58593/resume_upload_error.js`,
        };
        pardotFormSubmission(callbackName, pardotFormUrl, resumeCallbackLocations, resumeData, (error, response) => {
          if (error || !response || !response.success) {
            trackFormError({
              err: error,
              res: response,
              formName: 'Resume',
              message: 'Error submitting resume url to Pardot [v3]',
            });
            // Retrieves all cached data and slow submits
            submitAllDataToSlowForm(page, JobProfile);
          }
        });
      }
    });
  };

  const previousData = getFormData(formName);

  const timer = useRef(null);
  const debounceTriggerValidation = (inputName, time = 600) => {
    window.clearTimeout(timer.current);
    const trigger = inputName ? () => triggerValidation(inputName) : triggerValidation;
    timer.current = window.setTimeout(trigger, time);
  };

  // Necessary because react-hook-form has a bug
  // regarding default values on required radio inputs
  useEffect(() => {
    if (previousData.hear_about_pathrise) {
      triggerValidation();
    }
  }, [previousData.hear_about_pathrise, triggerValidation]);

  const linkedInRegex = /^\s*(https?:\/\/)?(www\.)?linkedin\.com\/in\/\w+/;

  // `validate` requires a boolean value to be returned
  const atLeastOneField = () => {
    const formData = getValues();
    if (formData.linkedin_url || previousData.fellow_cv || formData.fellow_cv.length > 0) {
      return true;
    }
    return false;
  };

  // Hear About Us question
  const [hearAboutPathrise, setHearAboutPathrise] = useState(previousData.hear_about_pathrise);

  const renderHearAboutConditional = (selection) => {
    let label;
    let name;
    let placeholder;
    switch (selection) {
      case pathriseAlumni:
        label = 'Who referred you?';
        name = 'referring_fellow';
        placeholder = 'First & Last Name';
        break;
      case heardOther:
        label = 'Please Explain';
        name = 'referring_other';
        placeholder = '...';
        break;
      default:
        return null;
    }
    const required = hearAboutUsRequiredFields[selection];
    return (
      <>
        <EnrollmentFormInput
          name={name}
          type="text"
          label={label}
          placeholder={placeholder}
          defaultValue={previousData[name]}
          inputRef={register({ required })}
          required={required}
          error={errors[name]}
          onChange={debounceTriggerValidation}
        />
        <EnrollmentFormError errorField={errors[name]} />
      </>
    );
  };

  const uploadedFiles = watch('fellow_cv', []);

  return (
    <EnrollmentForm
      formName={formName}
      callbackLocations={callbackLocations}
      actionUrl={pardotFormUrl}
      formValid={formState.isValid}
      validationHandler={handleSubmit}
      preSubmit={preSubmit}
      asyncPreSubmit={uploadedFiles.length > 0}
    >
      <legend>Share your profile</legend>
      <p>
        To move forward with our admissions process, we ask that you share either your resume or a LinkedIn profile.{' '}
        <em>*</em>
      </p>
      <EnrollmentFormFileUpload
        name="fellow_cv"
        accept=".pdf,.doc,.docx"
        title="Resume Upload"
        helpText=".pdf, .doc, or .docx"
        defaultValue={previousData.fellow_cv}
        inputRef={register}
        onChange={() => triggerValidation('linkedin_url')}
      />
      {androidCheck() && (
        <p className={styles.warningText}>
          If your resume is not uploading correctly, please continue with your LinkedIn profile.
        </p>
      )}
      <p className={styles.divider}>OR</p>
      <EnrollmentFormInputFloating
        name="linkedin_url"
        type="url"
        label="LinkedIn Profile"
        imageLabel={linkedinLogo}
        placeholder="https://linkedin.com/in/profile"
        defaultValue={previousData.linkedin_url}
        watchInput={watch('linkedin_url')}
        error={errors.linkedin_url}
        inputRef={register({ pattern: linkedInRegex, maxLength: 255, validate: atLeastOneField })}
        onChange={() => debounceTriggerValidation('linkedin_url')}
      />
      <EnrollmentFormError
        errorField={errors.linkedin_url}
        errorText={{
          pattern: 'Please check that your LinkedIn profile url is correct',
          validate: 'Please either upload your resume or enter your LinkedIn Profile',
        }}
      />
      <div className={styles.lineDivider} />
      <EnrollmentFormInput
        name="phone"
        type="tel"
        label="Phone"
        placeholder="&nbsp;"
        defaultValue={previousData.phone}
        inputRef={register({ required: true, pattern: phoneRegex })}
        error={errors.phone}
        required
      />
      <EnrollmentFormError
        errorField={errors.phone}
        errorText={{ pattern: 'Please check that your phone number is correct' }}
      />
      <EnrollmentFormSelectInput
        name="hear_about_pathrise"
        label="How did you hear about Pathrise?"
        defaultValue={previousData.hear_about_pathrise}
        error={errors.hear_about_pathrise}
        inputRef={register({ required: true })}
        required
        onChange={(e) => {
          // Necessary to clear the input for react-hook-form to register errors properly
          setHearAboutPathrise(null);
          const { value } = e.target;
          window.setTimeout(() => {
            setHearAboutPathrise(value);
          }, 0);
          const debounceTime = hearAboutUsRequiredFields[value] ? 2000 : 0;
          debounceTriggerValidation(null, debounceTime);
        }}
      >
        {hearAboutOptions.map((opt) => (
          <option value={opt} key={opt}>
            {opt}
          </option>
        ))}
      </EnrollmentFormSelectInput>
      <EnrollmentFormError errorField={errors.hear_about_pathrise} />
      {renderHearAboutConditional(hearAboutPathrise)}
    </EnrollmentForm>
  );
};

export default JobProfileForm;
